/**
 * "Migrate existing project" — pure, framework-free form logic for MigrateProjectDialog.
 *
 * Mirrors the server contract in packages/projects/handlers/migrate-project.ts (strict body) and
 * packages/projects/services/migration.service.ts (tool vocabulary + §12.1 linkage rule). The
 * per-step validators here are UX only; the server re-validates everything.
 */
import axios from "axios";
import { isNamedLeadRole, type ProjectRole } from "@/types/intake";
import { PHASE_NAMES, phaseLabel } from "@/lib/phases";
import { PROJECT_TYPE_OPTIONS } from "@/lib/project-types";

export const MIGRATION_TOOLS = [
  "github",
  "slack",
  "jira",
  "sharepoint",
  "clockify",
] as const;
export type MigrationTool = (typeof MIGRATION_TOOLS)[number];

/** GitHub / Slack are §12.1 linkage fields — admin / leadership only (server re-enforces). */
export const LINKAGE_RESTRICTED_TOOLS: ReadonlySet<MigrationTool> = new Set([
  "github",
  "slack",
]);

export const TOOL_META: Record<
  MigrationTool,
  { label: string; placeholder: string; help: string }
> = {
  github: {
    label: "GitHub repository",
    placeholder: "https://github.com/CLDNT/acme-portal or acme-portal",
    help: "Must already exist in the CLDNT organization.",
  },
  slack: {
    label: "Slack channel",
    placeholder: "C0123ABCD or #acme-delivery",
    help: "Channel id or name. For a private channel, add the DeliverPro Slack app to it first.",
  },
  jira: {
    label: "Jira project",
    placeholder: "ACME or https://<site>.atlassian.net/browse/ACME",
    help: "Project key or project URL.",
  },
  sharepoint: {
    label: "SharePoint site or folder URL",
    placeholder:
      "https://<tenant>.sharepoint.com/sites/<site>[/Shared Documents/<folder>]",
    help: "The project's site, or its folder inside a shared site (e.g. one folder per customer). Format is checked; DeliverPro doesn't verify it exists.",
  },
  clockify: {
    label: "Clockify project",
    placeholder: "64f0… (24 characters) or app.clockify.me/projects/… URL",
    help: "Project id or its Clockify URL.",
  },
};

/** CASDM phase options ("Phase 0 — Internal Preparation" …), derived from lib/phases. */
export const PHASE_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  PHASE_NAMES.map((_, n) => ({ value: String(n), label: phaseLabel(n) }));

/** Re-exported from lib/project-types, the single source of truth. */
export { PROJECT_TYPE_OPTIONS };

export const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "On Hold", label: "On Hold" },
] as const;

export const MIGRATION_STEPS = [
  "Project basics",
  "Integrations",
  "SOW",
  "Current phase",
  "Team",
  "Review",
] as const;

/** POST /api/projects/migrate/validate response. */
export interface IntegrationCheckResult {
  tool: MigrationTool;
  valid: boolean;
  checked: boolean;
  normalized: string | null;
  display: string | null;
  message: string;
  code?: string;
}

/** Per-field Validate state. `validatedValue` pins the result to the exact text that was checked. */
export interface ToolValidation {
  status: "idle" | "checking" | "valid" | "invalid";
  validatedValue?: string;
  result?: IntegrationCheckResult;
  /** Request-level failure (network / 403) — distinct from a "not found" result. */
  error?: string;
}

export interface TeamRow {
  /** Stable React key. */
  key: string;
  resourceId: string;
  resourceName: string;
  role: ProjectRole;
}

export interface MigrationForm {
  title: string;
  clientName: string;
  projectType: string;
  status: "Active" | "On Hold";
  kickoff: string;
  completion: string;
  /** T-48/49: the project's current planned IPR date (YYYY-MM-DD). */
  ipr: string;
  description: string;
  projectKey: string;
  integrations: Record<MigrationTool, string>;
  sowUrl: string;
  sowHours: string;
  hoursConsumed: string;
  /** T-48/49: links to the SRS / kickoff deck the project already has (https). */
  srsUrl: string;
  kickoffDeckUrl: string;
  currentPhase: string;
  team: TeamRow[];
}

export const EMPTY_MIGRATION_FORM: MigrationForm = {
  title: "",
  clientName: "",
  projectType: "AppDev",
  status: "Active",
  kickoff: "",
  completion: "",
  ipr: "",
  description: "",
  projectKey: "",
  integrations: {
    github: "",
    slack: "",
    jira: "",
    sharepoint: "",
    clockify: "",
  },
  sowUrl: "",
  sowHours: "",
  hoursConsumed: "",
  srsUrl: "",
  kickoffDeckUrl: "",
  currentPhase: "",
  team: [],
};

export type FieldErrors = Partial<Record<string, string>>;

const PROJECT_KEY_RE = /^[A-Za-z][A-Za-z0-9]*(?:-[A-Za-z0-9]+)*$/;
const NUMBER_RE = /^\d+(\.\d+)?$/;
/** Mirrors SOW_URL_RE in migrate-project.ts and the V055 https CHECKs. */
const HTTPS_URL_RE = /^https:\/\/\S+$/i;

/** Step 1 — basics. Returns field → message. */
export function validateBasics(form: MigrationForm): FieldErrors {
  const errors: FieldErrors = {};
  if (!form.title.trim()) errors.title = "Project name is required.";
  if (!form.clientName.trim())
    errors.clientName = "Client / company name is required.";
  if (
    form.projectKey.trim() &&
    (!PROJECT_KEY_RE.test(form.projectKey.trim()) ||
      form.projectKey.trim().length < 2 ||
      form.projectKey.trim().length > 20)
  ) {
    errors.projectKey =
      "Use 2–20 letters, digits or single hyphens, starting with a letter (e.g. ACME-01).";
  }
  if (form.kickoff && form.completion && form.completion < form.kickoff) {
    errors.completion =
      "Expected completion can't be before the planned kickoff.";
  }
  if (form.description.length > 4000) {
    errors.description = "Description must be 4,000 characters or fewer.";
  }
  return errors;
}

/** Tools with a value that the caller is allowed to submit. */
export function suppliedTools(
  form: MigrationForm,
  canLinkRestricted: boolean,
): MigrationTool[] {
  return MIGRATION_TOOLS.filter(
    (t) =>
      form.integrations[t].trim() !== "" &&
      (canLinkRestricted || !LINKAGE_RESTRICTED_TOOLS.has(t)),
  );
}

/**
 * Step 2 — every filled-in integration must have a passing Validate result for its CURRENT text.
 * Returns field → message (field = the tool).
 */
export function validateIntegrations(
  form: MigrationForm,
  validations: Partial<Record<MigrationTool, ToolValidation>>,
  canLinkRestricted: boolean,
): FieldErrors {
  const errors: FieldErrors = {};
  for (const tool of suppliedTools(form, canLinkRestricted)) {
    const v = validations[tool];
    const value = form.integrations[tool].trim();
    if (
      !v ||
      v.validatedValue !== value ||
      v.status === "idle" ||
      v.status === "checking"
    ) {
      errors[tool] =
        `Validate the ${TOOL_META[tool].label.toLowerCase()} before continuing, or clear it.`;
    } else if (v.status !== "valid") {
      errors[tool] =
        v.error ??
        v.result?.message ??
        `The ${TOOL_META[tool].label.toLowerCase()} didn't pass validation.`;
    }
  }
  return errors;
}

/** Step 3 — SOW. */
export function validateSow(form: MigrationForm): FieldErrors {
  const errors: FieldErrors = {};
  const url = form.sowUrl.trim();
  if (url && !HTTPS_URL_RE.test(url)) {
    errors.sowUrl = "SOW link must be an https:// URL.";
  }
  const srs = form.srsUrl.trim();
  if (srs && !HTTPS_URL_RE.test(srs)) {
    errors.srsUrl = "SRS link must be an https:// URL.";
  }
  const deck = form.kickoffDeckUrl.trim();
  if (deck && !HTTPS_URL_RE.test(deck)) {
    errors.kickoffDeckUrl = "Kickoff deck link must be an https:// URL.";
  }
  const hours = form.sowHours.trim();
  if (hours && (!NUMBER_RE.test(hours) || Number(hours) <= 0)) {
    errors.sowHours = "SOW hours must be a positive number.";
  }
  const used = form.hoursConsumed.trim();
  if (used && !NUMBER_RE.test(used)) {
    errors.hoursConsumed = "Hours consumed must be zero or a positive number.";
  }
  return errors;
}

/** Step 4 — a phase must be chosen. */
export function validatePhase(form: MigrationForm): FieldErrors {
  return /^[0-4]$/.test(form.currentPhase)
    ? {}
    : { currentPhase: "Select the project's current CASDM phase." };
}

/** Step 5 — team rows: every row needs a person, ≤ 1 PM, ≤ 1 SA, no duplicate person+role. */
export function validateTeam(team: TeamRow[]): FieldErrors {
  const errors: FieldErrors = {};
  team.forEach((row, i) => {
    if (!row.resourceId)
      errors[`team.${i}`] = "Pick a person or remove this row.";
  });
  if (team.filter((r) => r.role === "Project Manager").length > 1) {
    errors.team = "A project can have only one Project Manager.";
  } else if (team.filter((r) => r.role === "Solution Architect").length > 1) {
    errors.team = "A project can have only one Solution Architect.";
  } else {
    const seen = new Set<string>();
    for (const r of team) {
      const k = `${r.resourceId}|${r.role}`;
      if (r.resourceId && seen.has(k)) {
        errors.team = "The same person is listed twice with the same role.";
        break;
      }
      seen.add(k);
    }
  }
  return errors;
}

/** Whether the ResourcePicker for this role should require timesheet-submitting people. */
export function requiresAssignable(role: string): boolean {
  return !isNamedLeadRole(role);
}

/** POST /api/projects/migrate body. */
export interface MigrateProjectPayload {
  title: string;
  client_name: string;
  project_type: string;
  status: "Active" | "On Hold";
  planned_kickoff_date?: string;
  expected_completion_date?: string;
  planned_ipr_date?: string;
  existing_srs_url?: string;
  existing_kickoff_deck_url?: string;
  description?: string;
  project_key?: string;
  integrations: Partial<Record<MigrationTool, string>>;
  sow: {
    url?: string;
    file?: { filename: string; content_type: string; size_bytes: number };
    hours?: number;
    hours_consumed?: number;
  };
  current_phase: number;
  team: Array<{ resource_id: string; role_on_project: ProjectRole }>;
}

/** Build the strict payload. Integrations send the server-normalized value from Validate. */
export function buildMigratePayload(
  form: MigrationForm,
  validations: Partial<Record<MigrationTool, ToolValidation>>,
  canLinkRestricted: boolean,
  sowFile: File | null,
): MigrateProjectPayload {
  const integrations: Partial<Record<MigrationTool, string>> = {};
  for (const tool of suppliedTools(form, canLinkRestricted)) {
    integrations[tool] =
      validations[tool]?.result?.normalized ?? form.integrations[tool].trim();
  }
  const sow: MigrateProjectPayload["sow"] = {};
  if (form.sowUrl.trim()) sow.url = form.sowUrl.trim();
  if (form.sowHours.trim()) sow.hours = Number(form.sowHours);
  if (form.hoursConsumed.trim())
    sow.hours_consumed = Number(form.hoursConsumed);
  if (sowFile) {
    sow.file = {
      filename: sowFile.name,
      content_type: sowFile.type || "application/octet-stream",
      size_bytes: sowFile.size,
    };
  }
  return {
    title: form.title.trim(),
    client_name: form.clientName.trim(),
    project_type: form.projectType,
    status: form.status,
    ...(form.kickoff ? { planned_kickoff_date: form.kickoff } : {}),
    ...(form.completion ? { expected_completion_date: form.completion } : {}),
    ...(form.ipr ? { planned_ipr_date: form.ipr } : {}),
    ...(form.srsUrl.trim() ? { existing_srs_url: form.srsUrl.trim() } : {}),
    ...(form.kickoffDeckUrl.trim()
      ? { existing_kickoff_deck_url: form.kickoffDeckUrl.trim() }
      : {}),
    ...(form.description.trim()
      ? { description: form.description.trim() }
      : {}),
    ...(form.projectKey.trim()
      ? { project_key: form.projectKey.trim().toUpperCase() }
      : {}),
    integrations,
    sow,
    current_phase: Number(form.currentPhase),
    team: form.team
      .filter((r) => r.resourceId)
      .map((r) => ({ resource_id: r.resourceId, role_on_project: r.role })),
  };
}

/** Which wizard step owns a server-side error field (`details.field`). */
export function stepForField(field: string | undefined): number {
  if (!field) return MIGRATION_STEPS.length - 1;
  if (field.startsWith("integrations.")) return 1;
  if (field.startsWith("sow") || field.startsWith("existing_")) return 2;
  if (field === "current_phase") return 3;
  if (field.startsWith("team")) return 4;
  if (
    [
      "title",
      "client_name",
      "project_key",
      "project_type",
      "status",
      "planned_kickoff_date",
      "expected_completion_date",
      "planned_ipr_date",
      "description",
    ].includes(field)
  ) {
    return 0;
  }
  return MIGRATION_STEPS.length - 1;
}

export interface MappedMigrationError {
  message: string;
  field?: string;
  code?: string;
}

/** Turn a rejected migrate request into a message + the field it belongs to. */
export function mapMigrationError(
  err: unknown,
  kind: "migrate" | "validate" = "migrate",
): MappedMigrationError {
  if (!axios.isAxiosError(err)) {
    return {
      message:
        err instanceof Error ? err.message : "Migration failed. Try again.",
    };
  }
  if (!err.response) {
    return {
      message:
        kind === "validate"
          ? "The check couldn't be completed — the server didn't respond. Try again."
          : "The server didn't respond in time. Check the Projects list before retrying — the migration may have completed.",
    };
  }
  const body = err.response.data as
    | { code?: string; message?: string; details?: Record<string, unknown> }
    | undefined;
  const details = body?.details ?? {};
  let field = typeof details.field === "string" ? details.field : undefined;
  if (!field && body?.code === "VALIDATION_ERROR") {
    field = Object.keys(details)[0];
  }
  if (body?.code === "FORBIDDEN" && !field) {
    return {
      code: body.code,
      message:
        body.message ?? "You don't have permission to migrate this project.",
    };
  }
  return {
    code: body?.code,
    field,
    message: body?.message ?? "Migration failed. Try again.",
  };
}
